import React, { useEffect, useState } from "react";

import {
  fetchPCDashboard,
  fetchPCSubmissions,
  PCDashboardResponse,
  PCSubmissionQueueItem,
  submitPCSupervisoryIntervention,
} from "@/api/pcApi";
import {
  AvailableAction,
  fetchAvailableActions,
  formatWorkflowActionLabel,
} from "@/api/workflowApi";
import { formatWorkflowStatusLabel } from "@/utils/semanticLabels";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

const StatCard = ({
  label,
  value,
  tone = "text-gray-950",
}: {
  label: string;
  value: number;
  tone?: string;
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-sm text-muted-foreground">{label}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
    </CardContent>
  </Card>
);

function formatProjectionLabel(value: string | null | undefined): string {
  return formatWorkflowStatusLabel(value);
}

interface InterventionDraft {
  actions: AvailableAction[];
  actionCode: string;
  reason: string;
  loading: boolean;
  submitting: boolean;
  error: string;
}

const emptyDraft = (): InterventionDraft => ({
  actions: [],
  actionCode: "",
  reason: "",
  loading: false,
  submitting: false,
  error: "",
});

const PCDashboard: React.FC = () => {
  const [summary, setSummary] = useState<PCDashboardResponse | null>(null);
  const [queue, setQueue] = useState<PCSubmissionQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drafts, setDrafts] = useState<Record<number, InterventionDraft>>({});

  const updateDraft = (
    submissionId: number,
    patch: Partial<InterventionDraft>
  ) => {
    setDrafts((prev) => ({
      ...prev,
      [submissionId]: {
        ...(prev[submissionId] ?? emptyDraft()),
        ...patch,
      },
    }));
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryRes, queueRes] = await Promise.all([
        fetchPCDashboard(),
        fetchPCSubmissions(),
      ]);
      setSummary(summaryRes);
      setQueue(queueRes.items);
    } catch (err) {
      console.error(err);
      setError("Could not load PC workspace.");
    } finally {
      setLoading(false);
    }
  };

  const loadInterventionActions = async (submissionId: number) => {
    updateDraft(submissionId, { loading: true, error: "", actions: [], actionCode: "" });
    try {
      const response = await fetchAvailableActions(submissionId);
      updateDraft(submissionId, {
        actions: response.actions ?? [],
        actionCode: "",
        loading: false,
      });
    } catch (err: any) {
      updateDraft(submissionId, {
        loading: false,
        error: err?.response?.data?.detail ?? "Failed to load backend actions.",
      });
    }
  };

  const submitIntervention = async (submissionId: number) => {
    const draft = drafts[submissionId] ?? emptyDraft();
    const reason = draft.reason.trim();
    if (!draft.actionCode || !reason) return;
    if (!draft.actions.some((action) => action.action_code === draft.actionCode)) {
      updateDraft(submissionId, {
        error: "Workflow state changed. Reload backend actions.",
      });
      return;
    }

    updateDraft(submissionId, { submitting: true, error: "" });
    try {
      await submitPCSupervisoryIntervention(submissionId, {
        action_code: draft.actionCode,
        intervention_reason: reason,
      });
      setDrafts((prev) => ({
        ...prev,
        [submissionId]: emptyDraft(),
      }));
      await load();
    } catch (err: any) {
      updateDraft(submissionId, {
        submitting: false,
        error: err?.response?.data?.detail ?? "Supervisory intervention failed.",
      });
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Project Coordinator Workspace</h1>
          <p className="text-sm text-muted-foreground">
            Tenant-scoped coordination for DDO workload and aging assignments.
          </p>
        </div>
        <Button variant="outline" onClick={() => void load()}>
          Refresh
        </Button>
      </div>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      {loading ? (
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, index) => (
            <Skeleton key={index} className="h-28 w-full" />
          ))}
        </div>
      ) : null}

      {summary && !loading ? (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <StatCard label="DDOs" value={summary.ddo_count} />
            <StatCard label="Active assignments" value={summary.active_assignments} />
            <StatCard
              label="Stale assignments"
              value={summary.stale_assignments}
              tone={summary.stale_assignments > 0 ? "text-amber-700" : "text-gray-950"}
            />
            <StatCard
              label="Assignment routing failures"
              value={summary.routing_failures}
              tone={summary.routing_failures > 0 ? "text-red-600" : "text-gray-950"}
            />
            <StatCard
              label="Workload Spread"
              value={summary.workload_spread}
              tone={summary.workload_spread >= 3 ? "text-amber-700" : "text-gray-950"}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>DDO Workload</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {summary.ddo_workloads.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No DDOs are mapped under this PC in the active tenant.
                </p>
              ) : null}
              {summary.ddo_workloads.map((ddo) => (
                <div
                  key={ddo.ddo_user_id}
                  className="grid gap-3 rounded-md border bg-white p-4 md:grid-cols-[1fr_repeat(3,0.7fr)] md:items-center"
                >
                  <div>
                    <div className="font-medium">{ddo.ddo_username}</div>
                    <div className="text-xs text-muted-foreground">
                      DDO #{ddo.ddo_user_id}
                    </div>
                  </div>
                  <div className="text-sm">
                    Active assignments {ddo.active_assignments}
                  </div>
                  <div className="text-sm">
                    Stale assignments {ddo.stale_assignments}
                  </div>
                  <div className="text-sm">
                    Locked submissions {ddo.locked_submissions}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Assigned Queue</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {queue.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No active assigned submissions in this PC tree.
                </p>
              ) : null}
              {queue.map((item) => (
                <div
                  key={item.submission_id}
                  className="rounded-md border bg-white p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">
                        Submission #{item.submission_id}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {item.assigned_to_username} · {item.program_code || "No program"}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">{item.status}</Badge>
                      <Badge variant="outline">{item.assignment_source}</Badge>
                      {item.age_days != null ? (
                        <Badge variant="outline">{item.age_days} days</Badge>
                      ) : null}
                      <Badge variant="outline">
                        {formatProjectionLabel(item.lifecycle_phase)}
                      </Badge>
                      <Badge variant="outline">
                        {formatProjectionLabel(item.review_state)}
                      </Badge>
                      <Badge variant="outline">
                        {formatProjectionLabel(item.actionability_state)}
                      </Badge>
                      {item.assignment_state === "stale" ? (
                        <Badge
                          variant="outline"
                          className="border-amber-300 text-amber-700"
                        >
                          Stale assignment
                        </Badge>
                      ) : null}
                      {item.locked ? (
                        <Badge
                          variant="outline"
                          className="border-red-300 text-red-700"
                        >
                          Locked
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                  {item.routing_reason ? (
                    <div className="mt-3 rounded border bg-slate-50 p-3 text-xs text-muted-foreground">
                      {item.routing_reason}
                    </div>
                  ) : null}
                  <div className="mt-3 text-xs text-muted-foreground">
                    Latest: {formatProjectionLabel(item.latest_governance_event)}
                    {item.latest_return_source
                      ? ` from ${formatProjectionLabel(item.latest_return_source)}`
                    : ""}{" "}
                    - {formatProjectionLabel(item.blocking_reason)} -{" "}
                    {item.executable_action_count} backend action
                    {item.executable_action_count === 1 ? "" : "s"}
                  </div>
                  <div className="mt-4 border-t pt-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium">
                          Supervisory intervention
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Load backend-discovered actions and record a mandatory reason.
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={drafts[item.submission_id]?.loading}
                        onClick={() => loadInterventionActions(item.submission_id)}
                      >
                        {drafts[item.submission_id]?.loading ? "Loading..." : "Load Actions"}
                      </Button>
                    </div>

                    {drafts[item.submission_id]?.error ? (
                      <p className="mt-2 text-sm text-red-600">
                        {drafts[item.submission_id]?.error}
                      </p>
                    ) : null}

                    {(drafts[item.submission_id]?.actions.length ?? 0) > 0 ? (
                      <div className="mt-3 space-y-3">
                        <div className="flex flex-wrap gap-2">
                          {drafts[item.submission_id]?.actions.map((action) => (
                            <Button
                              key={action.action_code}
                              type="button"
                              variant={
                                drafts[item.submission_id]?.actionCode === action.action_code
                                  ? "default"
                                  : "outline"
                              }
                              onClick={() =>
                                updateDraft(item.submission_id, {
                                  actionCode: action.action_code,
                                })
                              }
                            >
                              {formatWorkflowActionLabel(action.action_code)} → {formatWorkflowStatusLabel(action.to_status)}
                            </Button>
                          ))}
                        </div>
                        <Textarea
                          placeholder="Required intervention reason"
                          value={drafts[item.submission_id]?.reason ?? ""}
                          onChange={(event) =>
                            updateDraft(item.submission_id, {
                              reason: event.target.value,
                            })
                          }
                        />
                        <Button
                          type="button"
                          disabled={
                            !drafts[item.submission_id]?.actionCode ||
                            !drafts[item.submission_id]?.reason.trim() ||
                            drafts[item.submission_id]?.submitting
                          }
                          onClick={() => submitIntervention(item.submission_id)}
                        >
                          {drafts[item.submission_id]?.submitting
                            ? "Recording..."
                            : "Record Intervention"}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default PCDashboard;
